import { __extends, __awaiter, __generator, __assign } from 'tslib';
import * as React from 'react';
import { useLocalization } from '@shared-ui/localization-context';
import { debounce } from 'throttle-debounce';
import { classNames, liveAnnounce } from 'uitk-react-utils';
import { UitkFloatingFullWidthButton } from 'uitk-react-button';
import { UitkLayoutFlex, UitkLayoutFlexItem } from 'uitk-react-layout-flex';
import { UitkScrim, UitkScrimType } from 'uitk-react-scrim';
import { UitkSheetTransition, UitkSheet, UitkSheetContent } from 'uitk-react-sheet';
import { UitkToolbar } from 'uitk-react-toolbar';
import './adapters/ess-adapter.esm.js';
import './adapters/thirdparty-adapter-factory.esm.js';
import './adapters/thirdparty-adapter.esm.js';
import './adapters/google-adapter.esm.js';
import './adapters/mapbox-adapter.esm.js';
import { getSizeOfTypeaheadResults, filterTypeaheadResults, selectTypeaheadResultIndex } from './adapters/typeahead-results.esm.js';
import { UitkTypeaheadResults } from './uitk-typeahead-results.esm.js';
import styles from './uitk-typeahead.css';
import { UitkTypeaheadInput } from './uitk-typeahead-input.esm.js';
var getClassNameList = classNames.bind(styles);
var TypeaheadNavKeys;
(function (TypeaheadNavKeys) {
  TypeaheadNavKeys["UP"] = "UP";
  TypeaheadNavKeys["DOWN"] = "DOWN";
})(TypeaheadNavKeys || (TypeaheadNavKeys = {}));
var TypeaheadDialog = /** @class */function (_super) {
  __extends(TypeaheadDialog, _super);
  function TypeaheadDialog(props) {
    var _this = _super.call(this, props) || this;
    _this.dialogRef = React.createRef();
    _this.toolbarInputRef = React.createRef();
    _this.iphoneSafariUARegex = /iphone.*safari|safari.*iphone/gi;
    _this.uid = Math.random().toString(36).substring(7);
    _this.typeaheadDialogLiveAnnounceId = "uitk-typeahead-dialog-live-announce-".concat(_this.uid);
    _this.typingTimeout = 0;
    _this.newAttr = -1;
    _this._isMounted = false;
    _this.state = {
      activeSuggestion: -1,
      categorizedResults: [],
      // eslint-disable-next-line react/no-unused-state
      data: {},
      results: [],
      searchTerm: props.term,
      showResults: true,
      // eslint-disable-next-line react/no-unused-state
      startSearchTerm: props.startSearchTerm
    };
    _this.announceResults = _this.announceResults.bind(_this);
    _this.focusInput = _this.focusInput.bind(_this);
    _this.handleChange = _this.handleChange.bind(_this);
    _this.handleKeyPress = _this.handleKeyPress.bind(_this);
    _this.handleKeyDown = _this.handleKeyDown.bind(_this);
    _this.handleKeyUp = _this.handleKeyUp.bind(_this);
    _this.handleAnnounceResults = _this.handleAnnounceResults.bind(_this);
    _this.handleResultItemSelect = _this.handleResultItemSelect.bind(_this);
    _this.onDismiss = _this.onDismiss.bind(_this);
    _this.openDialog = _this.openDialog.bind(_this);
    _this.updateResults = _this.updateResults.bind(_this);
    _this.updateResultsDebounced = props.debounceRate && props.debounceRate > 0 ? debounce(props.debounceRate, _this.updateResults) : _this.updateResults;
    _this.updateInput = _this.updateInput.bind(_this);
    _this.selectInput = _this.selectInput.bind(_this);
    _this.resetActiveSuggestion = _this.resetActiveSuggestion.bind(_this);
    _this.removeDialogLiveAnnounce = _this.removeDialogLiveAnnounce.bind(_this);
    _this.contentRef = React.createRef();
    return _this;
  }
  TypeaheadDialog.prototype.componentDidMount = function () {
    this._isMounted = true;
  };
  TypeaheadDialog.prototype.componentDidUpdate = function (prevProps, prevState) {
    var term = this.props.term;
    if (term !== prevProps.term) {
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({
        searchTerm: term
      });
    }
    var _a = this.state,
      searchTerm = _a.searchTerm,
      showResults = _a.showResults;
    if (prevState.searchTerm !== searchTerm) {
      if (showResults) {
        // eslint-disable-next-line react/no-did-update-set-state
        this.setState({
          results: TypeaheadDialog.placeholder
        });
        this.updateResultsDebounced(searchTerm);
      }
    }
  };
  TypeaheadDialog.prototype.componentWillUnmount = function () {
    var adapter = this.props.adapter;
    if (adapter && typeof adapter.logTypeaheadDismiss === 'function') {
      adapter.logTypeaheadDismiss();
    }
    this._isMounted = false;
  };
  TypeaheadDialog.prototype.handleChange = function (event) {
    var onInputChange = this.props.onInputChange;
    this.setState({
      searchTerm: event.target.value,
      showResults: true
    }, this.focusInput);
    // To reset activeSuggestion when value is cleared
    if (event.target.value === '') {
      this.resetActiveSuggestion();
    }
    onInputChange && onInputChange(event);
  };
  TypeaheadDialog.prototype.handleResultItemSelect = function (term, data, results) {
    var _this = this;
    var _a;
    var _b = this.props,
      isMulti = _b.isMulti,
      adapter = _b.adapter,
      l10n = _b.l10n,
      label = _b.label,
      onResultItemSelect = _b.onResultItemSelect;
    var searchTerm = this.state.searchTerm;
    return (_a = adapter.onItemSelection) === null || _a === void 0 ? void 0 : _a.call(adapter, data).then(function (result) {
      if (isMulti) {
        _this.setState({
          activeSuggestion: -1
        });
      } else {
        _this.setState({
          activeSuggestion: -1,
          searchTerm: term,
          showResults: false
        });
      }
      onResultItemSelect(term, result);
      // Results is the results prior to us triggering onResulItemSelect so we subtract 1
      // This is technically not correct because the typeahead is controlled and users could decide to not select the value (or do any other logic) in onChange even if that would not make sense.  Ideally liveAnnnounce logic would trigger based on state change and not on events
      var searchLen = results ? getSizeOfTypeaheadResults(results) - 1 : 0;
      if (isMulti) {
        liveAnnounce("".concat(l10n.formatText("typeahead.announceResults.itemSelected", term), "\n             ").concat(l10n.formatText('typeahead.announceResults.showingResults', label, searchLen, searchTerm)), undefined, undefined, _this.contentRef, _this.typeaheadDialogLiveAnnounceId);
      }
      if (!isMulti) {
        setTimeout(function () {
          liveAnnounce("".concat(l10n.formatText("typeahead.announceResults.itemSelected", term), "\n             ").concat(l10n.formatText('typeahead.announceResults.menuClosed')));
        }, 100);
        _this.onDismiss();
      }
    }).catch(function () {
      _this.updateResults(term, true, term !== '');
    });
  };
  TypeaheadDialog.prototype.handleAnnounceResults = function (results) {
    var _this = this;
    if (this.typingTimeout) {
      clearTimeout(this.typingTimeout);
    }
    // Delay by 1 second to ensure live announce is heard
    if (results.length > 0) {
      this.typingTimeout = setTimeout(function () {
        _this.announceResults(results);
      }, 1000);
    }
  };
  TypeaheadDialog.prototype.handleKeyPress = function (event) {
    var value = this.props.value;
    var resultsToShow = filterTypeaheadResults(this.getResultsToShow(), Array.isArray(value) ? value : []);
    var _a = this.state,
      activeSuggestion = _a.activeSuggestion,
      searchTerm = _a.searchTerm;
    if (event.key === 'Enter' || event.keyCode === 38 || event.keyCode === 40) {
      event.preventDefault();
    }
    if (event.key === 'Enter') {
      var selected = selectTypeaheadResultIndex(resultsToShow, activeSuggestion);
      selected ? this.handleResultItemSelect(selected.value, selected.data, resultsToShow) : this.handleResultItemSelect(searchTerm, undefined, resultsToShow);
    }
  };
  TypeaheadDialog.prototype.handleActiveSuggestion = function (direction) {
    var _a = this.state,
      activeSuggestion = _a.activeSuggestion,
      results = _a.results,
      searchTerm = _a.searchTerm;
    var allowExactMatch = this.props.allowExactMatch;
    // Extra list item in case of allowExactMatch true
    var extraListItemForSearch = allowExactMatch && searchTerm ? 1 : 0;
    var resultsLength = results.length + extraListItemForSearch;
    var circularListFactor = 0;
    if (direction === TypeaheadNavKeys.UP) {
      circularListFactor = activeSuggestion === -1 ? resultsLength : resultsLength - 1;
    } else if (direction === TypeaheadNavKeys.DOWN) {
      circularListFactor = resultsLength + 1;
    }
    var activePosition = (activeSuggestion + circularListFactor) % resultsLength;
    var activePositionTab = (this.newAttr + circularListFactor) % resultsLength;
    this.setState({
      activeSuggestion: this.newAttr > -1 ? activePositionTab : activePosition
    });
    this.newAttr = -1;
  };
  TypeaheadDialog.prototype.handleUpArrowKey = function (e) {
    if (e.keyCode === 38) {
      this.handleActiveSuggestion(TypeaheadNavKeys.UP);
    }
  };
  TypeaheadDialog.prototype.handleDownArrowKey = function (e) {
    if (e.keyCode === 40) {
      this.handleActiveSuggestion(TypeaheadNavKeys.DOWN);
    }
  };
  TypeaheadDialog.prototype.handleTabKey = function (e) {
    if (e.keyCode === 9 || e.shiftKey && e.keyCode === 9) {
      var attr = e.target.parentElement.getAttribute('data-index');
      if (attr) {
        this.newAttr = Number(attr);
      } else {
        this.newAttr = -1;
      }
    }
  };
  TypeaheadDialog.prototype.handleEscKey = function (e) {
    if (e.keyCode === 27) {
      this.onDismiss();
    }
  };
  TypeaheadDialog.prototype.handleKeyDown = function (e) {
    this.handleDownArrowKey(e);
    this.handleUpArrowKey(e);
    this.handleEscKey(e);
  };
  TypeaheadDialog.prototype.handleKeyUp = function (e) {
    this.handleTabKey(e);
  };
  TypeaheadDialog.prototype.onDismiss = function () {
    var _a = this.props,
      onDismiss = _a.onDismiss,
      isMulti = _a.isMulti,
      l10n = _a.l10n;
    this.contentRef.current.scrollTop = 0;
    if (onDismiss) {
      onDismiss();
    }
    // To reset activeSuggestion when typeahead closed
    this.resetActiveSuggestion();
    // eslint-disable-next-line react/no-unused-state
    this.setState({
      startSearchTerm: ''
    });
    if (isMulti) {
      setTimeout(function () {
        liveAnnounce(l10n.formatText('typeahead.announceResults.menuClosed'));
      }, 100);
    }
    this.removeDialogLiveAnnounce();
  };
  TypeaheadDialog.prototype.getResultsToShow = function () {
    var categorize = this.props.categorize;
    var _a = this.state,
      categorizedResults = _a.categorizedResults,
      results = _a.results;
    return categorize || categorizedResults && categorizedResults.length ? {
      items: categorizedResults,
      type: 'categorized'
    } : {
      items: results,
      type: 'uncategorized'
    };
  };
  TypeaheadDialog.prototype.removeDialogLiveAnnounce = function () {
    var _a;
    var liveAnnounceNode = document.getElementById(this.typeaheadDialogLiveAnnounceId);
    if (liveAnnounceNode) {
      (_a = liveAnnounceNode.parentNode) === null || _a === void 0 ? void 0 : _a.removeChild(liveAnnounceNode);
    }
  };
  TypeaheadDialog.prototype.resetActiveSuggestion = function () {
    this.setState({
      activeSuggestion: -1
    });
  };
  TypeaheadDialog.prototype.updateInput = function (term, makeAltDestCall) {
    var _this = this;
    this.setState({
      searchTerm: term
    }, function () {
      _this.updateResults(term, false, !!makeAltDestCall);
      setTimeout(function () {
        _this.selectInput();
      });
    });
  };
  TypeaheadDialog.prototype.scrollTop = function () {
    if (navigator.userAgent.match(this.iphoneSafariUARegex)) {
      if (document.getElementsByTagName('html')[0].scrollTop > 0) {
        document.getElementsByTagName('html')[0].scrollTo({
          top: 0,
          behavior: 'smooth'
        });
      }
    }
  };
  TypeaheadDialog.prototype.openDialog = function () {
    var searchTerm = this.state.searchTerm;
    var _a = this.props,
      adapter = _a.adapter,
      startSearchTerm = _a.startSearchTerm,
      defaultTerm = _a.defaultTerm;
    adapter.typeaheadDialogOpened(searchTerm);
    this.updateInput(searchTerm || defaultTerm || startSearchTerm || '', searchTerm !== '');
    this.focusInput();
    this.scrollTop();
  };
  TypeaheadDialog.prototype.selectInput = function () {
    var toolbarInputRef = this.toolbarInputRef;
    if (toolbarInputRef && toolbarInputRef.current && typeof toolbarInputRef.current.select === 'function') {
      toolbarInputRef.current.select();
    }
  };
  TypeaheadDialog.prototype.focusInput = function () {
    var toolbarInputRef = this.toolbarInputRef;
    if (toolbarInputRef && toolbarInputRef.current && typeof toolbarInputRef.current.focus === 'function') {
      toolbarInputRef.current.focus();
    }
  };
  TypeaheadDialog.prototype.announceResults = function (results) {
    var searchTerm = this.state.searchTerm;
    var _a = this.props,
      l10n = _a.l10n,
      label = _a.label,
      defaultTerm = _a.defaultTerm;
    var searchChars = searchTerm && searchTerm.split('');
    if (searchChars) {
      var outputWithQuotes = "'".concat(searchChars.join("' , '"), " '");
      if (results) {
        liveAnnounce(l10n.formatText("typeahead.announceResults.resultsAvailable", outputWithQuotes, results.length), undefined, undefined, this.contentRef, this.typeaheadDialogLiveAnnounceId);
      }
    } else if (defaultTerm) {
      if (results) {
        liveAnnounce(l10n.formatText("typeahead.announceResults.showingResultsDefault", label, results.length));
      }
    }
  };
  TypeaheadDialog.prototype.isCategorizedResult = function (result) {
    return 'heading' in result;
  };
  TypeaheadDialog.prototype.updateResults = function (term, makeRecentHistoryCall, makeAltDestCall) {
    return __awaiter(this, void 0, void 0, function () {
      var _a, adapter, categorize, defaultTerm, termOrDefault, results, categorizedResults, _b, queryResults, recentHistoryResults, _c, tempResults, tempResults, allResults;
      var _this = this;
      return __generator(this, function (_d) {
        switch (_d.label) {
          case 0:
            _a = this.props, adapter = _a.adapter, categorize = _a.categorize, defaultTerm = _a.defaultTerm;
            termOrDefault = term === '' ? defaultTerm !== null && defaultTerm !== void 0 ? defaultTerm : '' : term;
            this.lastSearchTerm = termOrDefault;
            if (!(termOrDefault !== '' && makeRecentHistoryCall === true)) return [3 /*break*/, 5];
            if (!categorize) return [3 /*break*/, 2];
            return [4 /*yield*/, Promise.all([adapter.fetchResultsCategorized(termOrDefault), adapter.fetchRecentSearchResultsCategorized()])];
          case 1:
            _c = _d.sent();
            return [3 /*break*/, 4];
          case 2:
            return [4 /*yield*/, Promise.all([adapter.fetchResults(termOrDefault), adapter.fetchRecentSearchResults()])];
          case 3:
            _c = _d.sent();
            _d.label = 4;
          case 4:
            _b = _c, queryResults = _b[0], recentHistoryResults = _b[1];
            if (recentHistoryResults && recentHistoryResults.length > 0) {
              categorize || this.isCategorizedResult(recentHistoryResults[0]) ? categorizedResults = recentHistoryResults : results = recentHistoryResults;
            } else {
              categorize ? categorizedResults = queryResults : results = queryResults;
            }
            return [3 /*break*/, 11];
          case 5:
            if (!categorize) return [3 /*break*/, 7];
            return [4 /*yield*/, adapter.fetchResultsCategorized(termOrDefault)];
          case 6:
            categorizedResults = _d.sent();
            return [3 /*break*/, 11];
          case 7:
            if (!(termOrDefault !== '' && !!makeAltDestCall)) return [3 /*break*/, 9];
            return [4 /*yield*/, adapter.fetchAlternativeResults(termOrDefault)];
          case 8:
            tempResults = _d.sent();
            tempResults && tempResults.length && this.isCategorizedResult(tempResults[0]) ? categorizedResults = tempResults : results = tempResults;
            return [3 /*break*/, 11];
          case 9:
            return [4 /*yield*/, adapter.fetchResults(termOrDefault)];
          case 10:
            tempResults = _d.sent();
            tempResults && tempResults.length && this.isCategorizedResult(tempResults[0]) ? categorizedResults = tempResults : results = tempResults;
            _d.label = 11;
          case 11:
            if (this.lastSearchTerm !== termOrDefault) {
              return [2 /*return*/];
            }
            if (results && results.length && this._isMounted) {
              adapter.logSuggestionShown(makeRecentHistoryCall || makeAltDestCall ? '' : termOrDefault, results);
              this.setState({
                results: results,
                categorizedResults: []
              }, function () {
                _this.handleAnnounceResults(results);
              });
            } else if (categorizedResults && categorizedResults.length) {
              adapter.logSuggestionShown(makeRecentHistoryCall || makeAltDestCall ? '' : termOrDefault, categorizedResults);
              allResults = categorizedResults.reduce(function (finalArray, currentResult) {
                finalArray.push.apply(finalArray, currentResult.typeaheadResults);
                return finalArray;
              }, []);
              this.setState({
                categorizedResults: categorizedResults,
                results: allResults
              }, function () {
                _this.handleAnnounceResults(categorizedResults);
              });
            } else if (this._isMounted) {
              this.setState({
                results: [],
                categorizedResults: []
              });
            }
            return [2 /*return*/];
        }
      });
    });
  };
  TypeaheadDialog.prototype.render = function () {
    var _a = this.props,
      allowExactMatch = _a.allowExactMatch,
      dialog = _a.dialog,
      id = _a.id,
      listTitle = _a.listTitle,
      stid = _a.stid,
      placeholder = _a.placeholder,
      label = _a.label,
      handleClear = _a.handleClear,
      emptyResultPlaceholder = _a.emptyResultPlaceholder,
      isMulti = _a.isMulti,
      dismissButtonText = _a.dismissButtonText,
      clearAllText = _a.clearAllText,
      onClearAll = _a.onClearAll,
      onItemRemove = _a.onItemRemove,
      selectedItemHeading = _a.selectedItemHeading,
      value = _a.value,
      strictFullscreen = _a.strictFullscreen,
      typeaheadInputRef = _a.typeaheadInputRef,
      required = _a.required;
    var _b = this.state,
      activeSuggestion = _b.activeSuggestion,
      searchTerm = _b.searchTerm;
    var resultsToShow = this.getResultsToShow();
    return React.createElement(UitkSheetTransition, {
      isVisible: dialog,
      openCallback: this.openDialog
    }, React.createElement(UitkSheet, {
      className: getClassNameList('uitk-typeahead-dialog', {
        'uitk-typeahead-dialog-centered': !strictFullscreen
      }),
      isVisible: dialog,
      type: strictFullscreen ? 'full' : 'centered',
      triggerRef: typeaheadInputRef
    }, React.createElement(UitkLayoutFlex, {
      direction: "column",
      blockSize: "full_size"
    }, React.createElement(UitkLayoutFlexItem, null, React.createElement(UitkToolbar, {
      className: getClassNameList('uitk-typeahead-input-wrapper'),
      navigationContent: {
        navIconLabel: 'Close the dialog',
        onClick: this.onDismiss,
        navButtonId: "".concat(id, "-close")
      }
    }, React.createElement(UitkTypeaheadInput, {
      className: getClassNameList('uitk-typeahead-input-v2'),
      "data-stid": stid && "".concat(stid, "-dialog-input"),
      domRef: this.toolbarInputRef,
      hasClearButton: searchTerm.length > 0,
      hasFloatedLabel: false,
      hasVisibleLabel: false,
      id: stid,
      label: label,
      onChange: this.handleChange,
      onKeyPress: this.handleKeyPress,
      placeholder: placeholder,
      value: searchTerm,
      onKeyDown: this.handleKeyDown,
      onKeyUp: this.handleKeyUp,
      handleClear: handleClear,
      required: required
    }))), React.createElement(UitkLayoutFlexItem, null, React.createElement(UitkSheetContent, {
      className: getClassNameList('uitk-typeahead-dialog-content'),
      contentDomRef: this.contentRef,
      padded: false
    }, React.createElement(UitkLayoutFlex, {
      direction: "column",
      blockSize: "full_size",
      justifyContent: "space-between"
    }, React.createElement(UitkLayoutFlexItem, null, React.createElement(UitkTypeaheadResults, {
      allowExactMatch: allowExactMatch,
      typeaheadLabel: label,
      onResultItemSelect: this.handleResultItemSelect,
      results: resultsToShow,
      searchTerm: searchTerm,
      stid: stid,
      listTitle: listTitle,
      activeSuggestion: activeSuggestion,
      emptyResultPlaceholder: emptyResultPlaceholder,
      isMulti: isMulti,
      dismissButtonText: dismissButtonText,
      selectedItemHeading: selectedItemHeading,
      clearAllText: clearAllText,
      onClearAll: onClearAll,
      onItemRemove: onItemRemove,
      value: value,
      contentRef: this.contentRef,
      historyDetail: this.props.historyDetail
    })), this.props.isMulti ? React.createElement(UitkLayoutFlexItem, null, React.createElement(UitkScrim, {
      type: UitkScrimType.BUTTON
    }, React.createElement(UitkFloatingFullWidthButton, {
      disabled: (value === null || value === void 0 ? void 0 : value.length) === 0,
      onClick: this.onDismiss,
      isFullWidth: true
    }, dismissButtonText))) : null))))));
  };
  TypeaheadDialog.placeholder = [{
    disabled: true,
    icon: 'brightness_1',
    name: ' ',
    value: ' ',
    title: ' '
  }];
  return TypeaheadDialog;
}(React.Component);
var UitkTypeaheadDialog = function (props) {
  var l10n = useLocalization();
  return React.createElement(TypeaheadDialog, __assign({
    l10n: l10n
  }, props));
};
export { UitkTypeaheadDialog };